import api from "../services/api.js";
import { GET_SIGNATURE } from "./apiConstants.js";

export const isUUID = (uuid) => {
  const uuidRegex =
    /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/;
  return uuidRegex.test(uuid);
};

export const validateEmail = (email) => {
  const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
  return emailRegex.test(email);
};

export const validatePassword = (password) => {
  const passwordRegex =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]+$/;
  return passwordRegex.test(password);
};

export const getApiErrorMessage = (error, fallback = "API Error") => {
  const responseData = error?.response?.data;

  if (typeof responseData === "string") {
    return responseData;
  }

  return responseData?.message || error?.message || fallback;
};

export const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return { status: true, message: "text copied successfully" };
  } catch (error) {
    return { status: false, message: "failed to copy text" };
  }
};

export const titleCase = (str) => {
  return str
    .toLowerCase()
    .split(" ")
    .map(function (word) {
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
};

export const uploadFileToCloudinary = async (inputFile, type = "raw") => {
  try {
    const maxSize = 10 * 1024 * 1024; // 10 MB
    if (inputFile.size > maxSize) {
      throw new Error("Image size exceeds the 10 MB limit.");
    }

    const signatureResponse = await api({
      url: GET_SIGNATURE,
      method: "get",
    });

    const { signature, timestamp, apiKey, cloudName } = signatureResponse?.data?.data;

    const formData = new FormData();
    formData.append("file", inputFile);
    formData.append("api_key", apiKey);
    formData.append("timestamp", timestamp);
    formData.append("signature", signature);

    const cloudRes = await fetch(
      `https://api.cloudinary.com/v1_1/${cloudName}/${type}/upload`,
      {
        method: "POST",
        body: formData,
      }
    );

    const cloudJson = await cloudRes.json();
    const fileUrl = cloudJson.secure_url;

    return {
      url: fileUrl,
      imagePublicId: cloudJson.public_id,
      imageResourceType: cloudJson.resource_type,
    };
  } catch (err) {
    throw new Error(
      err?.message || "Unexpected error while uploading file to Cloudinary"
    );
  }
};

export const getInitials = ({ text, limit = null, upperCase = true }) => {
  if (!text) return "";
  let initials = text
    .split(" ")
    .filter((word) => word.length > 0)
    .map((word) => word[0]);

  if (limit !== null) {
    initials = initials.slice(0, limit);
  }

  let result = initials.join("");

  if (upperCase) {
    result = result.toUpperCase();
  }

  return result;
};

export const base64ToUint8Array = (base64) => {
  const clean = base64.replace(/^data:application\/pdf;base64,/, "");
  const binaryString = atob(clean);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
};

export const getDynamicFontSize = (width, height, scale = 0.15) => {
  const base = Math.min(width, height);
  const fontSize = Math.max(5, Math.floor(base * scale));
  return fontSize;
};

export function calculateExperience(startedYear) {
  if (!startedYear) return 0;

  const today = new Date();

  // If only year provided (e.g. "2002")
  if (/^\d{4}$/.test(startedYear)) {
    return today.getFullYear() - Number(startedYear);
  }

  // If full date provided
  const start = new Date(startedYear);

  let years = today.getFullYear() - start.getFullYear();

  const hasNotHadAnniversaryYet =
    today.getMonth() < start.getMonth() ||
    (today.getMonth() === start.getMonth() &&
      today.getDate() < start.getDate());

  if (hasNotHadAnniversaryYet) years--;

  return years;
}

export const getCloudinaryAvatar = (url, size = 70) => {
  if (!url) return url;

  return url.replace(
    "/upload/",
    `/upload/w_${size},h_${size},c_fill,f_auto,q_auto/`
  );
};

export const isIframeRoot = () => {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
};

export const findAndReplaceURLWithAnchorTags = (text) => {
  const urlRegex =
    /(?:https?:\/\/)?(?:www\.)?[a-zA-Z0-9-]+(?:\.[a-zA-Z]{2,})+(?:\/[^\s,)]*)?/g;
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = urlRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    // Strip trailing sentence punctuation that isn't part of the URL
    const raw = match[0].replace(/[.,;:!?]+$/, "");
    const href = raw.startsWith("http") ? raw : `https://${raw}`;
    const trailingPunct = match[0].slice(raw.length); // restore stripped chars as plain text
    parts.push(
      <a
        key={match.index}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        // className={styles.answerLink}
      >
        {raw}
      </a>
    );
    if (trailingPunct) parts.push(trailingPunct);
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
};
